import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { DoseDistribution } from './dose/DoseDistribution';
import { ImageObject } from './image/ImageObject';
import { StructureSetObject } from './structure/StructureSetObject';

/**
 * A volume placed in the primary image's frame for the views that show it
 * alone: through `registration`, or as it is when the two share a frame of
 * reference. Held while any view holds it.
 */
export interface AlignedImageSlot {
    readonly object: ImageObject;
    readonly registration: RigidRegistration | undefined;
    holders: number;
}

/**
 * {@link Environment} is a storage class, storing the state of the objects that are currently being viewed.\
 * Environment also stores the common viewing parameters shared between the views.
 */
export class EnvironmentObjects {
    // Primary image being displayed in the viewer. This defines the basic coordinate system (Frame of Reference) for the viewer.
    public primaryImage: ImageObject | undefined;

    // Secondary image being displayed in the viewer.
    // The secondary image must include a transformation
    // to the frame of reference of the primary image to be rendered correctly.
    public secondaryImage: ImageObject | undefined;

    public structureSet: StructureSetObject | undefined;

    public dose: DoseDistribution | undefined;

    public registration: RigidRegistration | undefined;

    // Volumes placed in the primary's frame for views that show one alone,
    // keyed by {@link alignedImageKey}. Apart from the fused secondary, so a
    // fusion and any number of aligned views coexist.
    public readonly alignedImages = new Map<string, AlignedImageSlot>();
}

/**
 * The key of the volume `seriesUid` aligned through `registrationId`, or
 * through no registration (a shared frame of reference) when undefined.
 */
export function alignedImageKey(
    seriesUid: string,
    registrationId: string | undefined
): string {
    return `${seriesUid}|${registrationId ?? 'identity'}`;
}
